import { getLogger } from '@lib/logging';

const logger = getLogger('MoneyUtils');

/**
 * Cache for Intl.NumberFormat formatters. Use {@link:createMoneyFormatterHashKey} to get the key
 */
export const formatters: Record<string, any> = {};

/**
 * Creates a composite cache key for `formatMoney`.
 *
 * @returns {string} - cache key for the formatter
 */
export const createMoneyFormatterHashKey = (
  /* Locale to use */
  locale: string,
  /* Other options (for advanced formatting) */
  options: Record<string, string | number> = {},
): string => {
  return `${locale}_${JSON.stringify(options)}`;
};

/**
 * Formats a monetary value.
 *
 * Light wrapper around Intl.NumberFormat
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat

 * @returns {string} - Formatted monetary amount
 */
export const formatMoney = (
  /* Monetary amount to format (in dollars or currency equivalent) */
  amount: number,
  /* Whether to include cents */
  includeCents = false,
  /* 3-letter currency code. Defaults to `USD` */
  currency?: string,
  /* locale to use */
  locale = 'en-US',
  /* misc other options to pass to NumberFormat. Will override previous settings. */
  options?: Record<string, string | number>,
): string => {
  // Do not allow arbitrary locales to prevent potential memory leak
  let validatedLocale;
  try {
    validatedLocale = Intl.getCanonicalLocales(locale);
  } catch (e) {
    logger.warn(
      `Money formatter received invalid locale ${locale} -- falling back to 'en-US'`,
    );
    validatedLocale = 'en-US';
  }

  const formatterOptions = {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: includeCents ? 2 : 0,
    minimumFractionDigits: includeCents ? 2 : 0,
    ...options,
  };

  const key = createMoneyFormatterHashKey(locale, formatterOptions);
  let formatter = formatters[key];
  if (!formatter) {
    formatter = new Intl.NumberFormat(validatedLocale, formatterOptions);
    formatters[key] = formatter;
  }

  // Explicitly fall back to 0. Otherwise, `-0` may be displayed.
  return formatter.format(amount || 0);
};

export function toDollars(
  num: number,
  currency?: string,
  includeCents = false,
) {
  return formatMoney(num ?? 0, includeCents, currency);
}

/**
 * Formats a number to a number with optional numeric abbreviation.
 *
 * For absolute values <`threshold`, no abbreviation will be added.
 * For absolute values >=`threshold`, the value will be rounded, formatted, and abbreviated.
 *
 * For example:
 *    100000 = $100K
 *    -99999 = -$100K
 *    100200 = $100.2K
 *    1500000 = $1.5M
 */
export const toAbbreviatedDollars = (
  // Amount to format
  value: number,
  // Only format values greater than or equal to this (Must be positive)
  threshold: number = Number.NEGATIVE_INFINITY,
  // For abbreviated values (exceeding the threshold), how many decimals
  // to allow (e.g. 4143 and threshold of 1K with a decimalPrecision of 1
  // would display $4.1k, whereas a decimalPrecision of 0 would return $4k)
  decimalPrecision: number = 1,
  // The currency to use
  currency?: string,
): string => {
  const shouldAbbreviateNegativeNumber =
    value < 0 && Math.round(value) > threshold * -1;
  const shouldAbbreviatePositiveNumber =
    value >= 0 && Math.round(value) < threshold;

  if (shouldAbbreviateNegativeNumber || shouldAbbreviatePositiveNumber) {
    return toDollars(value);
  }

  return formatMoney(value, false, currency, 'en-US', {
    maximumFractionDigits: decimalPrecision,
    notation: 'compact',
  });
};
